'use client';

import { createClientLogger, logClientWarning } from '@/client/logging/client-logger';
import { useAnswerTemplates } from '@/components/answer-templates/use-answer-templates';
import { AutoReplyScenarioHeader } from '@/components/auto-replies/AutoReplyScenarioHeader';
import { AUTO_REPLY_DEFAULT_SCENARIO_NAME } from '@/components/auto-replies-copy';
import { ModalAnswerTemplatesEditorFromBindings } from '@/components/modal-answer-picked/ModalAnswerTemplatesEditor';
import type {
    AutoReplyActionMode,
    AutoReplyScenarioEntity,
    AutoReplyScenarioPayload,
    AutoReplySendMode,
} from '@/contracts/marketplace/auto-replies';
import { useAuth } from '@/context/auth.context';
import { useMarketplaceApi } from '@/context/marketplace.context';
import { useReviewsPageDataContext } from '@/context/reviews-page.context';
import { UIPriority } from '@/enums/ui-priorities';
import {
    buildReportBrandGroupOptions,
    buildReportPlatformOptions,
    buildReportProductGroupOptions,
    normalizeNextReportFilterSelection,
    REPORT_FILTER_ALL_VALUE,
    resolveAppliedReportFilterValues,
} from '@/components/products-report-modal/report-modal-filters';
import {
    normalizeIsoDateFilter,
    toIsoDateInGmtPlus3,
    toUtcInclusiveEndIsoFromGmtPlus3Date,
    toUtcStartIsoFromGmtPlus3Date,
} from '@/shared/date-time-gmt-plus-3';
import { extractApiErrorMessageFromUnknown } from '@/shared/errors/api-error';
import type { RuntimeSupportedMarketplace } from '@/shared/marketplace';
import { buildReviewAiReviewTypeOptions, type ReviewAiReviewType } from '@/shared/review-ai-review-types';
import Button from '@/ui/Button';
import DatePicker from '@/ui/DatePicker';
import Input from '@/ui/Input';
import Select, { type SelectOption } from '@/ui/Select';
import Textarea from '@/ui/Textarea';
import { toast } from '@/ui/toast';
import clsx from 'clsx';
import { type DragEvent, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

type AutoReplyScenarioDraft = AutoReplyScenarioEntity & {
    isNew?: boolean;
};

const AUTO_REPLIES_LOAD_ERROR = 'Не удалось загрузить сценарии автоответов';
const AUTO_REPLIES_SAVE_ERROR = 'Не удалось сохранить сценарий';
const AUTO_REPLIES_DELETE_ERROR = 'Не удалось удалить сценарий';
const AUTO_REPLIES_REORDER_ERROR = 'Не удалось изменить порядок сценариев';
const AUTO_REPLIES_EMPTY_LABEL = 'Сценариев пока нет';

const autoRepliesPanelLogger = createClientLogger({
    scope: 'auto-replies-panel',
});

const actionModeOptions: Array<{ value: Exclude<AutoReplyActionMode, 'none'>; label: string }> = [
    { value: 'ai', label: 'Сгенерировать ИИ-ответ' },
    { value: 'templates', label: 'Выбор ответа из шаблона(ов)' },
];

const sendModeOptions: Array<{ value: AutoReplySendMode; label: string }> = [
    { value: 'manual_approval', label: 'Требуется ручное одобрение' },
    { value: 'send_immediately', label: 'Отправлять ответ сразу после формирования' },
];

const reviewTypeOptions: SelectOption[] = [
    ...buildReviewAiReviewTypeOptions({
        locale: 'ru',
        allLabel: 'Все типы обращений',
        allValue: REPORT_FILTER_ALL_VALUE,
    }),
];

const createLocalScenarioDraft = (index: number): AutoReplyScenarioDraft => {
    const now = new Date().toISOString();
    return {
        id: `new-auto-reply-${Date.now()}`,
        name: AUTO_REPLY_DEFAULT_SCENARIO_NAME,
        priority: index + 1,
        enabled: true,
        activeFrom: null,
        activeTo: null,
        platforms: [],
        productGroupIds: [],
        brandGroupIds: [],
        ratings: [],
        includeNoRating: false,
        reviewTypes: [],
        textMode: 'any',
        textTerms: [],
        excludedTextTerms: [],
        requirePhotos: false,
        actionMode: 'none',
        sendMode: 'manual_approval',
        templateIds: [],
        createdAt: now,
        updatedAt: now,
        isNew: true,
    };
};

const toPayload = (scenario: AutoReplyScenarioDraft): AutoReplyScenarioPayload => ({
    name: scenario.name,
    enabled: scenario.enabled,
    activeFrom: scenario.activeFrom,
    activeTo: scenario.activeTo,
    platforms: scenario.platforms,
    productGroupIds: scenario.productGroupIds,
    brandGroupIds: scenario.brandGroupIds,
    ratings: scenario.ratings,
    includeNoRating: scenario.includeNoRating,
    reviewTypes: scenario.reviewTypes,
    textMode: scenario.textMode,
    textTerms: scenario.textTerms,
    excludedTextTerms: scenario.excludedTextTerms,
    requirePhotos: scenario.requirePhotos,
    actionMode: scenario.actionMode,
    sendMode: scenario.sendMode,
    templateIds: scenario.templateIds,
});

const termsToText = (terms: string[]): string => terms.join('\n');

const parseTermsText = (value: string): string[] => [
    ...new Set(
        value
            .split(/[\n,]/)
            .map((term) => term.trim())
            .filter(Boolean),
    ),
];

const toAutoReplyDatePickerValue = (value: string | null): string => {
    if (!value) {
        return '';
    }

    return toIsoDateInGmtPlus3(value) ?? '';
};

const fromAutoReplyStartDateValue = (value: string): string | null => {
    const normalizedValue = normalizeIsoDateFilter(value);
    return normalizedValue ? toUtcStartIsoFromGmtPlus3Date(normalizedValue) : null;
};

const fromAutoReplyEndDateValue = (value: string): string | null => {
    const normalizedValue = normalizeIsoDateFilter(value);
    return normalizedValue ? toUtcInclusiveEndIsoFromGmtPlus3Date(normalizedValue) : null;
};

const toScenarioFilterSelectValues = (values: string[]): string[] =>
    values.length ? values : [REPORT_FILTER_ALL_VALUE];

const getSelectableOptionValues = (options: SelectOption[]): string[] =>
    options.filter((option) => option.value !== REPORT_FILTER_ALL_VALUE).map((option) => option.value);

const resolveScenarioFilterValues = <TValue extends string>({
    currentValues,
    nextValues,
    options,
}: {
    currentValues: TValue[];
    nextValues: string[];
    options: SelectOption[];
}): TValue[] => {
    const normalizedValues = normalizeNextReportFilterSelection({
        currentValues: toScenarioFilterSelectValues(currentValues),
        nextValues,
        availableValues: getSelectableOptionValues(options),
    });
    return (resolveAppliedReportFilterValues(normalizedValues) ?? []) as TValue[];
};

const mergeScenarioPriorities = (
    currentScenarios: AutoReplyScenarioDraft[],
    reorderedScenarios: AutoReplyScenarioEntity[],
): AutoReplyScenarioDraft[] => {
    const priorityById = new Map(reorderedScenarios.map((scenario) => [scenario.id, scenario.priority]));
    return currentScenarios.map((scenario) => ({
        ...scenario,
        priority: priorityById.get(scenario.id) ?? scenario.priority,
    }));
};

const restoreScenarioPriorities = (
    currentScenarios: AutoReplyScenarioDraft[],
    previousScenarios: AutoReplyScenarioDraft[],
): AutoReplyScenarioDraft[] => {
    const priorityById = new Map(previousScenarios.map((scenario) => [scenario.id, scenario.priority]));
    return currentScenarios.map((scenario) => ({
        ...scenario,
        priority: priorityById.get(scenario.id) ?? scenario.priority,
    }));
};

const reprioritizeScenarios = (orderedScenarios: AutoReplyScenarioDraft[]): AutoReplyScenarioDraft[] =>
    orderedScenarios.map((scenario, index) => ({
        ...scenario,
        priority: index + 1,
    }));

const moveScenarioByIndex = (
    scenarios: AutoReplyScenarioDraft[],
    sourceIndex: number,
    targetIndex: number,
): AutoReplyScenarioDraft[] => {
    if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex || targetIndex >= scenarios.length) {
        return scenarios;
    }

    const nextScenarios = [...scenarios];
    const [movedScenario] = nextScenarios.splice(sourceIndex, 1);
    if (!movedScenario) {
        return scenarios;
    }

    nextScenarios.splice(targetIndex, 0, movedScenario);
    return reprioritizeScenarios(nextScenarios);
};

type SettingsRowProps = {
    label: string;
    children: ReactNode;
    align?: 'center' | 'start';
};

const SettingsRow = ({ label, children, align = 'center' }: SettingsRowProps) => (
    <div
        className={clsx(
            'grid gap-2 md:grid-cols-[128px_minmax(0,1fr)]',
            align === 'start' ? 'md:items-start' : 'md:items-center',
        )}
    >
        <span className={clsx('text-xs font-medium text-slate-600', align === 'start' && 'pt-1')}>{label}</span>
        <div className="min-w-0">{children}</div>
    </div>
);

const setScenarioTextTerms = (draft: AutoReplyScenarioDraft, value: string) => {
    draft.textTerms = parseTermsText(value);
    if (draft.textTerms.length) {
        draft.textMode = 'contains';
        return;
    }

    if (draft.textMode === 'contains') {
        draft.textMode = 'any';
    }
};

const setScenarioNoTextMode = (draft: AutoReplyScenarioDraft, checked: boolean) => {
    if (checked) {
        draft.textMode = 'no_text';
        draft.textTerms = [];
        return;
    }

    draft.textMode = draft.textTerms.length ? 'contains' : 'any';
};

const replaceScenario = (
    scenarios: AutoReplyScenarioDraft[],
    scenarioId: string,
    nextScenario: AutoReplyScenarioDraft,
): AutoReplyScenarioDraft[] => scenarios.map((scenario) => (scenario.id === scenarioId ? nextScenario : scenario));

const toggleArrayValue = <T extends string | number>(values: T[], value: T): T[] =>
    values.includes(value) ? values.filter((item) => item !== value) : [...values, value];

type CheckboxLineProps = {
    checked: boolean;
    label: string;
    disabled?: boolean;
    onChange: (checked: boolean) => void;
};

const CheckboxLine = ({ checked, label, disabled, onChange }: CheckboxLineProps) => (
    <label className={clsx('inline-flex items-center gap-2 text-sm', disabled ? 'text-slate-400' : 'text-slate-700')}>
        <input
            type="checkbox"
            className="h-4 w-4 rounded border-slate-300"
            checked={checked}
            disabled={disabled}
            onChange={(event) => onChange(event.target.checked)}
        />
        <span>{label}</span>
    </label>
);

type RadioLineProps<T extends string> = {
    checked: boolean;
    label: string;
    value: T;
    name: string;
    disabled?: boolean;
    onChange: (value: T) => void;
};

const RadioLine = <T extends string>({ checked, label, value, name, disabled, onChange }: RadioLineProps<T>) => (
    <label className={clsx('inline-flex items-center gap-2 text-sm', disabled ? 'text-slate-400' : 'text-slate-700')}>
        <input
            type="radio"
            className="h-4 w-4 border-slate-300"
            name={name}
            checked={checked}
            disabled={disabled}
            onChange={() => onChange(value)}
        />
        <span>{label}</span>
    </label>
);

export const AutoRepliesPanel = () => {
    const { accessToken } = useAuth();
    const { reviewsApi } = useMarketplaceApi();
    const { products, brandGroups } = useReviewsPageDataContext();
    const templatesBindings = useAnswerTemplates({
        isShown: true,
        accessToken,
        loadOnShow: true,
    });

    const [scenarios, setScenarios] = useState<AutoReplyScenarioDraft[]>([]);
    const [expandedScenarioId, setExpandedScenarioId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSavingId, setIsSavingId] = useState<string | null>(null);
    const [isReordering, setIsReordering] = useState(false);
    const [errorText, setErrorText] = useState<string | null>(null);
    const [draggedScenarioId, setDraggedScenarioId] = useState<string | null>(null);

    const sortedScenarios = useMemo(
        () => [...scenarios].sort((left, right) => left.priority - right.priority),
        [scenarios],
    );
    const platformOptions = useMemo(() => buildReportPlatformOptions(), []);
    const productGroupOptions = useMemo(() => buildReportProductGroupOptions(products), [products]);
    const brandGroupOptions = useMemo(() => buildReportBrandGroupOptions(brandGroups), [brandGroups]);

    const updateScenario = useCallback((scenarioId: string, update: (scenario: AutoReplyScenarioDraft) => void) => {
        setScenarios((currentScenarios) =>
            currentScenarios.map((scenario) => {
                if (scenario.id !== scenarioId) {
                    return scenario;
                }

                const nextScenario = { ...scenario };
                update(nextScenario);
                return nextScenario;
            }),
        );
    }, []);

    const loadScenarios = useCallback(async () => {
        setIsLoading(true);
        setErrorText(null);

        try {
            const response = await reviewsApi.getAutoReplyScenarios({ authToken: accessToken });
            setScenarios(response.data);
            setExpandedScenarioId(null);
        } catch (error) {
            const message = extractApiErrorMessageFromUnknown(error) ?? AUTO_REPLIES_LOAD_ERROR;
            setErrorText(message);
            logClientWarning({
                logger: autoRepliesPanelLogger,
                message: '[client.auto-replies.list] Failed to load auto-reply scenarios.',
                error,
                context: {
                    operation: 'auto_replies_list',
                },
            });
        } finally {
            setIsLoading(false);
        }
    }, [accessToken, reviewsApi]);

    useEffect(() => {
        void loadScenarios();
    }, [loadScenarios]);

    const onAddScenario = useCallback(() => {
        const draft = createLocalScenarioDraft(scenarios.length);
        setScenarios((currentScenarios) => [...currentScenarios, draft]);
        setExpandedScenarioId(draft.id);
    }, [scenarios.length]);

    const onSaveScenario = useCallback(
        async (scenario: AutoReplyScenarioDraft) => {
            setIsSavingId(scenario.id);
            setErrorText(null);

            try {
                const response = scenario.isNew
                    ? await reviewsApi.createAutoReplyScenario(toPayload(scenario), { authToken: accessToken })
                    : await reviewsApi.updateAutoReplyScenario(scenario.id, toPayload(scenario), {
                          authToken: accessToken,
                      });
                setScenarios((currentScenarios) =>
                    replaceScenario(currentScenarios, scenario.id, {
                        ...response.data,
                        isNew: false,
                    }),
                );
                setExpandedScenarioId(response.data.id);
            } catch (error) {
                const message = extractApiErrorMessageFromUnknown(error) ?? AUTO_REPLIES_SAVE_ERROR;
                setErrorText(message);
                logClientWarning({
                    logger: autoRepliesPanelLogger,
                    message: '[client.auto-replies.save] Failed to save auto-reply scenario.',
                    error,
                    context: {
                        operation: scenario.isNew ? 'auto_replies_create' : 'auto_replies_update',
                        scenarioId: scenario.isNew ? undefined : scenario.id,
                    },
                });
            } finally {
                setIsSavingId(null);
            }
        },
        [accessToken, reviewsApi],
    );

    const onDeleteScenario = useCallback(
        async (scenario: AutoReplyScenarioDraft) => {
            if (scenario.isNew) {
                setScenarios((currentScenarios) => currentScenarios.filter((item) => item.id !== scenario.id));
                setExpandedScenarioId(null);
                return;
            }

            setIsSavingId(scenario.id);
            setErrorText(null);

            try {
                await reviewsApi.deleteAutoReplyScenario(scenario.id, { authToken: accessToken });
                setScenarios((currentScenarios) => currentScenarios.filter((item) => item.id !== scenario.id));
                setExpandedScenarioId(null);
            } catch (error) {
                const message = extractApiErrorMessageFromUnknown(error) ?? AUTO_REPLIES_DELETE_ERROR;
                setErrorText(message);
                logClientWarning({
                    logger: autoRepliesPanelLogger,
                    message: '[client.auto-replies.delete] Failed to delete auto-reply scenario.',
                    error,
                    context: {
                        operation: 'auto_replies_delete',
                        scenarioId: scenario.id,
                    },
                });
            } finally {
                setIsSavingId(null);
            }
        },
        [accessToken, reviewsApi],
    );

    const reorderScenarioByIndex = useCallback(
        async (sourceIndex: number, targetIndex: number) => {
            const reprioritizedScenarios = moveScenarioByIndex(sortedScenarios, sourceIndex, targetIndex);
            if (reprioritizedScenarios === sortedScenarios) {
                return;
            }

            const previousScenarios = sortedScenarios;
            setScenarios(reprioritizedScenarios);
            if (reprioritizedScenarios.some((scenario) => scenario.isNew)) {
                toast.warning('Сохраните новый сценарий перед изменением порядка');
                return;
            }

            setIsReordering(true);
            setErrorText(null);
            try {
                const response = await reviewsApi.reorderAutoReplyScenarios(
                    reprioritizedScenarios.map((scenario) => scenario.id),
                    { authToken: accessToken },
                );
                setScenarios((currentScenarios) => mergeScenarioPriorities(currentScenarios, response.data));
            } catch (error) {
                const message = extractApiErrorMessageFromUnknown(error) ?? AUTO_REPLIES_REORDER_ERROR;
                setErrorText(message);
                toast.warning(message);
                logClientWarning({
                    logger: autoRepliesPanelLogger,
                    message: '[client.auto-replies.reorder] Failed to reorder auto-reply scenarios.',
                    error,
                    context: {
                        operation: 'auto_replies_reorder',
                    },
                });
                setScenarios((currentScenarios) => restoreScenarioPriorities(currentScenarios, previousScenarios));
            } finally {
                setIsReordering(false);
            }
        },
        [accessToken, reviewsApi, sortedScenarios],
    );

    const onDropScenario = useCallback(
        async (sourceScenarioId: string | null, targetScenarioId: string) => {
            if (!sourceScenarioId || sourceScenarioId === targetScenarioId) {
                return;
            }

            const sourceIndex = sortedScenarios.findIndex((scenario) => scenario.id === sourceScenarioId);
            const targetIndex = sortedScenarios.findIndex((scenario) => scenario.id === targetScenarioId);
            await reorderScenarioByIndex(sourceIndex, targetIndex);
        },
        [reorderScenarioByIndex, sortedScenarios],
    );

    const onScenarioDragOver = useCallback((event: DragEvent<HTMLElement>) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    }, []);

    return (
        <div className="flex min-h-full w-full flex-col items-center bg-white p-3">
            <div className="mb-3 flex w-full max-w-[1320px] flex-wrap items-center gap-2">
                <Button
                    type="button"
                    priorityType={UIPriority.PRIMARY}
                    uiSize="compact"
                    className="w-fit px-3"
                    onClick={onAddScenario}
                    disabled={isLoading || isReordering}
                >
                    Добавить сценарий
                </Button>
                <Button
                    type="button"
                    priorityType={UIPriority.SECONDARY}
                    uiSize="compact"
                    className="w-fit px-3"
                    onClick={() => void loadScenarios()}
                    disabled={isLoading || isReordering}
                >
                    Обновить
                </Button>
                {isLoading && <span className="text-sm text-slate-500">Загрузка...</span>}
                {errorText && <span className="text-sm text-red-600">{errorText}</span>}
            </div>

            {!isLoading && sortedScenarios.length === 0 && (
                <div className="w-full max-w-[1320px] border-y border-slate-200 py-6 text-sm text-slate-500">
                    {AUTO_REPLIES_EMPTY_LABEL}
                </div>
            )}

            <div className="flex min-h-0 w-full max-w-[1320px] flex-1 flex-col gap-2 overflow-auto">
                {sortedScenarios.map((scenario, index) => {
                    const isExpanded = expandedScenarioId === scenario.id;
                    const scenarioTemplates = new Set(scenario.templateIds);
                    const isNoop = scenario.actionMode === 'none';

                    return (
                        <section
                            key={scenario.id}
                            data-testid={`auto-reply-scenario-${scenario.id}`}
                            className={clsx(
                                'border border-slate-200 bg-white transition-colors',
                                draggedScenarioId === scenario.id && 'opacity-60',
                            )}
                            onDragOver={onScenarioDragOver}
                            onDrop={(event) => {
                                event.preventDefault();
                                const sourceScenarioId =
                                    draggedScenarioId || event.dataTransfer.getData('text/plain') || null;
                                void onDropScenario(sourceScenarioId, scenario.id);
                                setDraggedScenarioId(null);
                            }}
                        >
                            <AutoReplyScenarioHeader
                                scenarioId={scenario.id}
                                position={index + 1}
                                name={scenario.name}
                                enabled={scenario.enabled}
                                expanded={isExpanded}
                                disabled={isReordering}
                                onToggleExpanded={() => setExpandedScenarioId(isExpanded ? null : scenario.id)}
                                onToggleEnabled={() =>
                                    updateScenario(scenario.id, (draft) => {
                                        draft.enabled = !draft.enabled;
                                    })
                                }
                                onDragStart={(event) => {
                                    if (isReordering) {
                                        event.preventDefault();
                                        return;
                                    }

                                    setDraggedScenarioId(scenario.id);
                                    event.dataTransfer.effectAllowed = 'move';
                                    event.dataTransfer.setData('text/plain', scenario.id);
                                }}
                                onDragEnd={() => setDraggedScenarioId(null)}
                            />

                            {isExpanded && (
                                <div
                                    id={`auto-reply-scenario-body-${scenario.id}`}
                                    className="grid gap-4 border-t border-slate-200 p-3 lg:grid-cols-[minmax(0,1fr)_minmax(320px,420px)]"
                                >
                                    <div className="flex min-w-0 flex-col gap-4">
                                        <div className="grid gap-3 rounded-md border border-slate-200 bg-slate-50 p-3">
                                            <label className="min-w-0 text-xs font-medium text-slate-600">
                                                Имя сценария
                                                <Input
                                                    uiSize="compact"
                                                    value={scenario.name}
                                                    onChange={(event) =>
                                                        updateScenario(scenario.id, (draft) => {
                                                            draft.name = event.target.value;
                                                        })
                                                    }
                                                    className="mt-1"
                                                />
                                            </label>
                                        </div>

                                        <fieldset className="grid gap-3 rounded-md border border-slate-200 bg-slate-50 p-3">
                                            <legend className="text-sm font-semibold text-slate-900">Триггер</legend>
                                            <SettingsRow label="Период действия">
                                                <div
                                                    data-testid="auto-reply-period-fields"
                                                    className="grid min-w-0 grid-cols-[auto_minmax(0,96px)_auto_minmax(0,96px)] items-center gap-x-1.5 whitespace-nowrap"
                                                >
                                                    <span className="text-xs text-slate-500">с</span>
                                                    <div
                                                        data-testid="auto-reply-period-from"
                                                        className="min-w-0 max-w-[96px]"
                                                    >
                                                        <DatePicker
                                                            aria-label="Действует с"
                                                            className="w-full"
                                                            selectedDate={toAutoReplyDatePickerValue(
                                                                scenario.activeFrom,
                                                            )}
                                                            setSelectedDate={(value) =>
                                                                updateScenario(scenario.id, (draft) => {
                                                                    draft.activeFrom =
                                                                        fromAutoReplyStartDateValue(value);
                                                                })
                                                            }
                                                            uiSize="compact"
                                                            placeholder=""
                                                        />
                                                    </div>
                                                    <span className="text-xs text-slate-500">по</span>
                                                    <div
                                                        data-testid="auto-reply-period-to"
                                                        className="min-w-0 max-w-[96px]"
                                                    >
                                                        <DatePicker
                                                            aria-label="Действует по"
                                                            className="w-full"
                                                            selectedDate={toAutoReplyDatePickerValue(scenario.activeTo)}
                                                            setSelectedDate={(value) =>
                                                                updateScenario(scenario.id, (draft) => {
                                                                    draft.activeTo = fromAutoReplyEndDateValue(value);
                                                                })
                                                            }
                                                            uiSize="compact"
                                                            placeholder=""
                                                        />
                                                    </div>
                                                </div>
                                            </SettingsRow>

                                            <SettingsRow label="Каналы">
                                                <Select
                                                    id={`auto-reply-${scenario.id}-platforms`}
                                                    ariaLabel="Фильтр платформ автоответов"
                                                    options={platformOptions}
                                                    values={toScenarioFilterSelectValues(scenario.platforms)}
                                                    onValuesChange={(nextValues) =>
                                                        updateScenario(scenario.id, (draft) => {
                                                            draft.platforms =
                                                                resolveScenarioFilterValues<RuntimeSupportedMarketplace>(
                                                                    {
                                                                        currentValues: draft.platforms,
                                                                        nextValues,
                                                                        options: platformOptions,
                                                                    },
                                                                );
                                                        })
                                                    }
                                                    multiple
                                                    uiSize="compact"
                                                    className="min-w-0"
                                                />
                                            </SettingsRow>

                                            <SettingsRow label="Товары">
                                                <Select
                                                    id={`auto-reply-${scenario.id}-product-groups`}
                                                    ariaLabel="Фильтр групп товаров автоответов"
                                                    options={productGroupOptions}
                                                    values={toScenarioFilterSelectValues(scenario.productGroupIds)}
                                                    onValuesChange={(nextValues) =>
                                                        updateScenario(scenario.id, (draft) => {
                                                            draft.productGroupIds = resolveScenarioFilterValues({
                                                                currentValues: draft.productGroupIds,
                                                                nextValues,
                                                                options: productGroupOptions,
                                                            });
                                                        })
                                                    }
                                                    multiple
                                                    uiSize="compact"
                                                    className="min-w-0"
                                                />
                                            </SettingsRow>

                                            <SettingsRow label="Бренды">
                                                <Select
                                                    id={`auto-reply-${scenario.id}-brand-groups`}
                                                    ariaLabel="Фильтр групп брендов автоответов"
                                                    options={brandGroupOptions}
                                                    values={toScenarioFilterSelectValues(scenario.brandGroupIds)}
                                                    onValuesChange={(nextValues) =>
                                                        updateScenario(scenario.id, (draft) => {
                                                            draft.brandGroupIds = resolveScenarioFilterValues({
                                                                currentValues: draft.brandGroupIds,
                                                                nextValues,
                                                                options: brandGroupOptions,
                                                            });
                                                        })
                                                    }
                                                    multiple
                                                    uiSize="compact"
                                                    className="min-w-0"
                                                />
                                            </SettingsRow>

                                            <SettingsRow label="Оценка" align="start">
                                                <div className="flex flex-wrap gap-3">
                                                    {[1, 2, 3, 4, 5].map((rating) => (
                                                        <CheckboxLine
                                                            key={rating}
                                                            checked={scenario.ratings.includes(rating)}
                                                            label={`${rating}`}
                                                            onChange={() =>
                                                                updateScenario(scenario.id, (draft) => {
                                                                    draft.ratings = toggleArrayValue(
                                                                        draft.ratings,
                                                                        rating,
                                                                    ).sort((left, right) => left - right);
                                                                })
                                                            }
                                                        />
                                                    ))}
                                                    <CheckboxLine
                                                        checked={scenario.includeNoRating}
                                                        label="Без оценки"
                                                        onChange={(checked) =>
                                                            updateScenario(scenario.id, (draft) => {
                                                                draft.includeNoRating = checked;
                                                            })
                                                        }
                                                    />
                                                </div>
                                            </SettingsRow>

                                            <SettingsRow label="Тип обращения">
                                                <Select
                                                    id={`auto-reply-${scenario.id}-review-types`}
                                                    ariaLabel="Фильтр типов обращений автоответов"
                                                    options={reviewTypeOptions}
                                                    values={toScenarioFilterSelectValues(scenario.reviewTypes)}
                                                    onValuesChange={(nextValues) =>
                                                        updateScenario(scenario.id, (draft) => {
                                                            draft.reviewTypes =
                                                                resolveScenarioFilterValues<ReviewAiReviewType>({
                                                                    currentValues: draft.reviewTypes,
                                                                    nextValues,
                                                                    options: reviewTypeOptions,
                                                                });
                                                        })
                                                    }
                                                    multiple
                                                    uiSize="compact"
                                                    className="min-w-0"
                                                />
                                            </SettingsRow>

                                            <SettingsRow label="Слова-минус" align="start">
                                                <Textarea
                                                    ariaLabel="Слова-минус"
                                                    value={termsToText(scenario.excludedTextTerms)}
                                                    setValue={(value) =>
                                                        updateScenario(scenario.id, (draft) => {
                                                            draft.excludedTextTerms = parseTermsText(value);
                                                        })
                                                    }
                                                    rows={3}
                                                    className="border border-slate-200 p-2"
                                                />
                                            </SettingsRow>

                                            <SettingsRow label="Слова в тексте" align="start">
                                                <div className="grid gap-2">
                                                    <CheckboxLine
                                                        checked={scenario.textMode === 'no_text'}
                                                        label="Отсутствуют"
                                                        onChange={(checked) =>
                                                            updateScenario(scenario.id, (draft) => {
                                                                setScenarioNoTextMode(draft, checked);
                                                            })
                                                        }
                                                    />
                                                    <Textarea
                                                        ariaLabel="Слова в тексте"
                                                        value={termsToText(scenario.textTerms)}
                                                        setValue={(value) =>
                                                            updateScenario(scenario.id, (draft) => {
                                                                setScenarioTextTerms(draft, value);
                                                            })
                                                        }
                                                        disabled={scenario.textMode === 'no_text'}
                                                        rows={4}
                                                        className="border border-slate-200 p-2"
                                                    />
                                                </div>
                                            </SettingsRow>

                                            <SettingsRow label="Фото">
                                                <div className="flex flex-wrap items-center gap-3">
                                                    <CheckboxLine
                                                        checked={scenario.requirePhotos}
                                                        label="Присутствуют"
                                                        onChange={(checked) =>
                                                            updateScenario(scenario.id, (draft) => {
                                                                draft.requirePhotos = checked;
                                                            })
                                                        }
                                                    />
                                                </div>
                                            </SettingsRow>
                                        </fieldset>
                                    </div>

                                    <fieldset className="flex min-w-0 flex-col gap-4 border-t border-slate-200 pt-3 lg:border-l lg:border-t-0 lg:pl-4 lg:pt-0">
                                        <legend className="text-sm font-semibold text-slate-900">Действия</legend>
                                        <CheckboxLine
                                            checked={isNoop}
                                            label="Ничего не делаем"
                                            onChange={(checked) =>
                                                updateScenario(scenario.id, (draft) => {
                                                    draft.actionMode = checked ? 'none' : 'ai';
                                                })
                                            }
                                        />

                                        <div className="grid gap-2">
                                            <p className="text-xs font-semibold text-slate-600">
                                                I. Как формируем ответ
                                            </p>
                                            {actionModeOptions.map((option) => (
                                                <RadioLine
                                                    key={option.value}
                                                    name={`action-mode-${scenario.id}`}
                                                    value={option.value}
                                                    label={option.label}
                                                    checked={scenario.actionMode === option.value}
                                                    onChange={(value) =>
                                                        updateScenario(scenario.id, (draft) => {
                                                            draft.actionMode = value;
                                                            if (value !== 'templates') {
                                                                draft.templateIds = [];
                                                            }
                                                        })
                                                    }
                                                />
                                            ))}
                                        </div>

                                        {scenario.actionMode === 'templates' && (
                                            <div className="grid gap-2">
                                                <p className="text-xs font-medium text-slate-600">Шаблоны</p>
                                                <div className="grid max-h-44 gap-2 overflow-auto border border-slate-200 p-2">
                                                    {templatesBindings.answerTemplates.map((template) => (
                                                        <CheckboxLine
                                                            key={template.id}
                                                            checked={scenarioTemplates.has(template.id)}
                                                            label={template.name}
                                                            onChange={() =>
                                                                updateScenario(scenario.id, (draft) => {
                                                                    draft.templateIds = toggleArrayValue(
                                                                        draft.templateIds,
                                                                        template.id,
                                                                    );
                                                                })
                                                            }
                                                        />
                                                    ))}
                                                    {!templatesBindings.answerTemplates.length &&
                                                        !templatesBindings.isTemplatesLoading && (
                                                            <p className="text-sm text-slate-500">Нет шаблонов</p>
                                                        )}
                                                    {templatesBindings.isTemplatesLoading && (
                                                        <p className="text-sm text-slate-500">Загрузка шаблонов...</p>
                                                    )}
                                                </div>
                                                <Button
                                                    type="button"
                                                    priorityType={UIPriority.SECONDARY}
                                                    uiSize="compact"
                                                    className="w-fit px-3"
                                                    onClick={() => void templatesBindings.onOpenTemplatesEditor()}
                                                >
                                                    Добавить шаблон
                                                </Button>
                                            </div>
                                        )}

                                        <div className="grid gap-2">
                                            <p className="text-xs font-semibold text-slate-600">
                                                II. Как отправляем ответ
                                            </p>
                                            {sendModeOptions.map((option) => (
                                                <RadioLine
                                                    key={option.value}
                                                    name={`send-mode-${scenario.id}`}
                                                    value={option.value}
                                                    label={option.label}
                                                    checked={scenario.sendMode === option.value}
                                                    disabled={isNoop}
                                                    onChange={(value) =>
                                                        updateScenario(scenario.id, (draft) => {
                                                            draft.sendMode = value;
                                                            if (draft.actionMode === 'none') {
                                                                draft.actionMode = 'ai';
                                                            }
                                                        })
                                                    }
                                                />
                                            ))}
                                        </div>

                                        <div className="mt-auto flex flex-wrap gap-2 border-t border-slate-200 pt-3">
                                            <Button
                                                type="button"
                                                priorityType={UIPriority.PRIMARY}
                                                uiSize="compact"
                                                className="w-fit px-3"
                                                onClick={() => void onSaveScenario(scenario)}
                                                disabled={isSavingId === scenario.id}
                                            >
                                                {isSavingId === scenario.id ? 'Сохраняем...' : 'Сохранить'}
                                            </Button>
                                            <Button
                                                type="button"
                                                priorityType={UIPriority.SECONDARY}
                                                uiSize="compact"
                                                className="w-fit px-3"
                                                onClick={() => void onDeleteScenario(scenario)}
                                                disabled={isSavingId === scenario.id}
                                            >
                                                Удалить
                                            </Button>
                                        </div>
                                    </fieldset>
                                </div>
                            )}
                        </section>
                    );
                })}
            </div>

            {templatesBindings.isTemplatesEditorShown && (
                <ModalAnswerTemplatesEditorFromBindings
                    bindings={templatesBindings}
                    isShown={templatesBindings.isTemplatesEditorShown}
                    setIsShown={(value) => {
                        if (!value) {
                            templatesBindings.onCloseTemplatesEditor();
                        }
                    }}
                />
            )}
        </div>
    );
};

export default AutoRepliesPanel;
